import type { FC, ReactNode } from 'react'
import type { TargetAndTransition, Transition, Variants } from 'motion/react'

import { Fragment } from 'react'
import { motion } from 'motion/react'
import { duration, easeOut } from '@/lib/motion'

export type SplitEffect = 'fade' | 'rise' | 'slide' | 'slide-left'

type SplitTag = 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4'

interface SplitTextProps {
    text: string
    effect?: SplitEffect
    by?: 'char' | 'word'
    trigger?: 'mount' | 'view'
    delay?: number
    stagger?: number
    as?: SplitTag
    className?: string
    accessible?: boolean
    amount?: number
}

const motionTags = {
    span: motion.span,
    p: motion.p,
    h1: motion.h1,
    h2: motion.h2,
    h3: motion.h3,
    h4: motion.h4
}

// Only transform and opacity move, and distances scale with the type size so a small label and a display heading travel alike.
const hiddenStates: Record<SplitEffect, TargetAndTransition> = {
    fade: { opacity: 0 },
    rise: { opacity: 0, y: '0.35em' },
    slide: { opacity: 0, x: '0.4em' },
    'slide-left': { opacity: 0, x: '-0.4em' }
}

const shownStates: Record<SplitEffect, TargetAndTransition> = {
    fade: { opacity: 1 },
    rise: { opacity: 1, y: 0 },
    slide: { opacity: 1, x: 0 },
    'slide-left': { opacity: 1, x: 0 }
}

const pieceTransition: Transition = {
    default: { duration: duration.slow, ease: easeOut },
    opacity: { duration: duration.base, ease: easeOut }
}

// The whole sequence never takes longer than this, however long the text is.
const MAX_STAGGER_TOTAL = 0.5

export const SplitText: FC<SplitTextProps> = ({
    text,
    effect = 'slide',
    by = 'char',
    trigger = 'view',
    delay = 0,
    stagger,
    as = 'span',
    className,
    accessible = true,
    amount = 0.4
}): ReactNode => {
    const Tag = motionTags[as] as typeof motion.span
    const words = text.split(' ')
    const pieces = by === 'char' ? text.replace(/\s/g, '').length : words.length
    const preferred = stagger ?? (by === 'char' ? 0.025 : 0.035)
    const step = Math.min(preferred, MAX_STAGGER_TOTAL / Math.max(pieces - 1, 1))

    const container: Variants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: step,
                delayChildren: delay
            }
        }
    }

    const piece: Variants = {
        hidden: hiddenStates[effect],
        visible: { ...shownStates[effect], transition: pieceTransition }
    }

    const triggerProps = trigger === 'mount'
        ? { animate: 'visible' }
        : { whileInView: 'visible', viewport: { once: true, amount } }

    return (
        <Tag
            className={className}
            initial='hidden'
            variants={container}
            {...triggerProps}
        >
            {accessible && (
                <span className='sr-only'>
                    {text}
                </span>
            )}

            <span aria-hidden='true'>
                {words.map((word, wordIndex) => (
                    <Fragment key={`${word}-${wordIndex}`}>
                        {by === 'char' ? (
                            <span className='whitespace-nowrap'>
                                {Array.from(word).map((char, charIndex) => (
                                    <motion.span
                                        key={`${char}-${charIndex}`}
                                        className='inline-block'
                                        variants={piece}
                                    >
                                        {char}
                                    </motion.span>
                                ))}
                            </span>
                        ) : (
                            <motion.span
                                className='inline-block'
                                variants={piece}
                            >
                                {word}
                            </motion.span>
                        )}

                        {wordIndex < words.length - 1 ? ' ' : null}
                    </Fragment>
                ))}
            </span>
        </Tag>
    )
}
